import './SelfPickupWalkthrough.css';
import React, { useEffect, useState, useRef, useCallback, useContext } from 'react';
import { createPortal } from 'react-dom';
import { translate } from '../../../../utils/translator';
import { MainTracker } from '../../../../PixelTrackers/MainTracker';
import { useCustomer } from '../../../../customHooks/useCustomer';
import { ChooseTreatmentContext } from '../FreeDosageLayoutV2';
import * as constants from '../constants';
import useIsMobile from '../../../../customHooks/useIsMobile';

// eslint-disable-next-line @typescript-eslint/no-var-requires
const lottie = require('lottie-web');
// eslint-disable-next-line @typescript-eslint/no-var-requires
const confettiAnimationData = require('../../../../assets/animations/confetti.json');


interface SelfPickupWalkthroughProps {
  language: string;
  isVisible: boolean;
  onClose: () => void;
  onComplete: () => void;
  step: number;
  onStepChange: (step: number) => void;
}

interface HighlightPosition {
  top: number;
  left: number;
  width: number;
  height: number;
}

const STEP_CONFIGS = [
  {
    elementId: 'self-pickup-tab-button',
    getElement: () => document.getElementById('self-pickup-tab-button'),
  },
  {
    elementId: 'pickup-service-options-container',
    getElement: () => document.getElementById('pickup-service-options-container'),
  },
  {
    elementId: 'pharmacy-search-section',
    getElement: () => document.getElementById('pharmacy-search-section'),
  },
  {
    elementId: 'location-button',
    getElement: () => {
      const buttons = Array.from(document.querySelectorAll('button'));
      const locationBtn = buttons.find((btn) => {
        const text = btn.textContent?.toLowerCase() || '';
        return (
          text.includes('current location') ||
          text.includes('aktuellen standort') ||
          text.includes('standort verwenden')
        );
      });
      if (locationBtn) return locationBtn;

      const buttonWithIcon = document
        .querySelector('button svg, button [class*="location"]')
        ?.closest('button');
      if (buttonWithIcon) return buttonWithIcon;

      return document.querySelector('[id*="location"], [id*="Location"]');
    },
  },
  {
    elementId: 'fast-doctor-review-container',
    getElement: () => document.getElementById('fast-doctor-review-container'),
  },
];

const HIGHLIGHT_PADDING = {
  step1: 16,
  step2: 16,
  step3: 16,
  step4: 0,
  step5: 16,
};

const SelfPickupWalkthrough: React.FC<SelfPickupWalkthroughProps> = ({
  language,
  isVisible,
  onClose,
  onComplete,
  step,
  onStepChange,
}) => {
  const { customerState } = useCustomer();
  const chooseTreatmentContext = useContext(ChooseTreatmentContext);
  const isMobile = useIsMobile();
  const totalSteps = (chooseTreatmentContext?.isFastApprovalFlow && chooseTreatmentContext?.isExpressFlow) ? 5 : 4;

  const [highlightPosition, setHighlightPosition] = useState<HighlightPosition | null>(null);
  const [highlightPosition2, setHighlightPosition2] = useState<HighlightPosition | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState<{ top: number; left: number } | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const overlayRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const confettiRef = useRef<HTMLDivElement>(null);
  const hasTrackedShown = useRef<boolean>(false);

  const isConfettiStep = step === 1
    && chooseTreatmentContext?.isCannabisReturningCustomer
    && chooseTreatmentContext?.isExpressFlow;

  useEffect(() => {
    if (!isConfettiStep || !confettiRef.current) return;
    const anim = lottie.loadAnimation({
      container: confettiRef.current,
      renderer: 'svg',
      loop: false,
      autoplay: true,
      animationData: confettiAnimationData,
    });
    return () => anim.destroy();
  }, [isConfettiStep]);

  const getPadding = () => HIGHLIGHT_PADDING[`step${step}` as keyof typeof HIGHLIGHT_PADDING] || 0;

  // Helper: Calculate highlight position with padding
  const calculateHighlightWithPadding = (position: HighlightPosition | null, padding: number) => {
    if (!position) return { top: 0, left: 0, width: 0, height: 0 };
    return {
      top: position.top - padding,
      left: position.left - padding,
      width: position.width + padding * 2,
      height: position.height + padding * 2,
    };
  };

  // Helper: Calculate modal position with viewport constraints
  const calculateModalPosition = (
    elementRect: DOMRect,
    tooltipOffset: number,
    modalWidth: number,
    viewportWidth: number,
    padding: number
  ) => {
    let tooltipTop = elementRect.bottom + tooltipOffset;
    let tooltipLeft = elementRect.left;

    if (tooltipLeft + modalWidth > viewportWidth - padding) {
      tooltipLeft = viewportWidth - modalWidth - padding;
    }
    if (tooltipLeft < padding) {
      tooltipLeft = padding;
    }

    return { top: tooltipTop, left: tooltipLeft };
  };

  const updateHighlightPosition = useCallback(
    (retryCount = 0) => {
      const currentStep = STEP_CONFIGS[step - 1];
      if (!currentStep) return;

      // For step 1, highlight both the pickup tab and express delivery card separately, position modal below express delivery card
      if (step === 1) {
        const pickupTab = document.getElementById('self-pickup-tab-button');
        const expressCard = document.getElementById('express-delivery-combined-container');

        if ((!pickupTab && !isConfettiStep) || !expressCard) {
          if (retryCount < 10) {
            setTimeout(() => updateHighlightPosition(retryCount + 1), 300);
          }
          return;
        }

        expressCard.scrollIntoView({ block: 'center', behavior: 'smooth' });

        setTimeout(() => {
          const expressRectAfter = expressCard.getBoundingClientRect();

          if (isConfettiStep) {
            setHighlightPosition(null);
          } else {
            const tabBarRect = pickupTab!.getBoundingClientRect();
            setHighlightPosition({
              top: tabBarRect.top,
              left: tabBarRect.left,
              width: tabBarRect.width,
              height: tabBarRect.height,
            });
          }

          setHighlightPosition2({
            top: expressRectAfter.top,
            left: expressRectAfter.left,
            width: expressRectAfter.width,
            height: expressRectAfter.height,
          });

          const tooltipOffset = 16;
          const modalWidth = isConfettiStep ? 269 : 228;
          const viewportWidth = window.innerWidth;
          const padding = 20;

          const highlightPad = HIGHLIGHT_PADDING.step1;
          const tooltipTop = expressRectAfter.bottom + highlightPad + tooltipOffset;
          let tooltipLeft: number;

          if (isConfettiStep && isMobile) {
            // Centered horizontally; the render applies translateX(-50%)
            tooltipLeft = viewportWidth / 2;
          } else {
            tooltipLeft = expressRectAfter.right - modalWidth;
            if (tooltipLeft < padding) tooltipLeft = padding;
            if (tooltipLeft + modalWidth > viewportWidth - padding) {
              tooltipLeft = viewportWidth - modalWidth - padding;
            }
          }

          setTooltipPosition({ top: tooltipTop, left: tooltipLeft });
          setIsTransitioning(false);
        }, 100);
        return;
      }

      const element = currentStep.getElement();
      if (!element) {
        if (retryCount < 10) {
          setTimeout(() => updateHighlightPosition(retryCount + 1), 300);
        }
        return;
      }

      element.scrollIntoView({ block: 'center', behavior: 'smooth' });

      setTimeout(() => {
        const rect = element.getBoundingClientRect();
        setHighlightPosition({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
        });

        const tooltipOffset = 16;
        const modalWidth = (step === 4 || step === 5) ? 320 : 360;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        const padding = 20;

        let tooltipTop = rect.bottom + tooltipOffset;
        let tooltipLeft = rect.left;

        // For steps 4 and 5, check if modal would be cut off at bottom of screen
        if (step === 4 || step === 5) {
          // Center horizontally on viewport
          tooltipLeft = viewportWidth / 2;

          // Get actual modal height if already rendered, otherwise estimate
          const modalHeight = tooltipRef.current?.offsetHeight || 180;

          // Check if there's enough space below the button
          const spaceBelow = viewportHeight - rect.bottom;
          if (spaceBelow < modalHeight + tooltipOffset + padding) {
            // Position above the element instead
            tooltipTop = rect.top - modalHeight - tooltipOffset;

            // If still not enough space above, position in viewport with visible spacing
            if (tooltipTop < padding) {
              tooltipTop = Math.max(
                padding,
                Math.min(rect.top - tooltipOffset, viewportHeight - modalHeight - padding)
              );
            }
          }
        } else {
          // Use helper for steps 1 and 2
          const modalPos = calculateModalPosition(rect, tooltipOffset, modalWidth, viewportWidth, padding);
          tooltipTop = modalPos.top;
          tooltipLeft = modalPos.left;
        }

        setTooltipPosition({ top: tooltipTop, left: tooltipLeft });

        setIsTransitioning(false);
      }, 100);
    },
    [step, isConfettiStep, isMobile]
  );

  useEffect(() => {
    if (!isVisible) return;

    const initialTimer = setTimeout(() => updateHighlightPosition(), 100);
    const handleResize = () => updateHighlightPosition();
    const handleScroll = () => updateHighlightPosition();

    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      clearTimeout(initialTimer);
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isVisible, step, updateHighlightPosition]);

  useEffect(() => {
    const expressCard = document.getElementById('express-delivery-combined-container');
    if (!expressCard) return;

    if (isVisible && step === 1) {
      expressCard.classList.add('walkthrough-active');
    } else {
      expressCard.classList.remove('walkthrough-active');
    }

    return () => {
      expressCard.classList.remove('walkthrough-active');
    };
  }, [isVisible, step]);

  const handleConfettiTry = () => {
    MainTracker.track('custom', 'walkthrough_action', {
      user_id: customerState?.customerID || null,
      action_type: 'confetti_try',
      step_number: step,
    });

    if (chooseTreatmentContext) {
      chooseTreatmentContext.setShippingMethod(constants.EXPRESS_SHIPPING);
      chooseTreatmentContext.setSelectedService(constants.SERVICE_PRESCRIPTION_AND_MEDICATION);
      chooseTreatmentContext.setSelectedApprovalService(constants.SERVICE_STANDARD_APPROVAL);
      chooseTreatmentContext.setIsExpressPrescriptionReview(false);
    }

    document.body.style.overflow = '';
    onClose();

    setTimeout(() => {
      const zipcodeSearchButton = document.getElementById('zipcode-search-button');
      if (zipcodeSearchButton) {
        zipcodeSearchButton.click();
      }
    }, 300);
  };

  const handleSkip = () => {
    MainTracker.track('custom', 'walkthrough_action', {
      user_id: customerState?.customerID || null,
      action_type: 'skip',
      step_number: step,
    });
    document.body.style.overflow = '';
    onClose();
  };

  const hideCurrentStepUI = () => {
    setTooltipPosition(null);
    setHighlightPosition(null);
    setHighlightPosition2(null);
  };

  const goToStep = (nextStep: number, delayMs = 0) => {
    setIsTransitioning(true);
    hideCurrentStepUI();

    // Let React flush the "hidden" render before changing step
    setTimeout(() => {
      onStepChange(nextStep);
    }, delayMs);
  };

  const handleContinue = () => {
    // Track
    const actionType = step === totalSteps ? 'finish' : 'continue';
    MainTracker.track('custom', 'walkthrough_action', {
      user_id: customerState?.customerID || null,
      action_type: actionType,
      step_number: step,
    });

    if (step === 1 && chooseTreatmentContext) {
      const {
        setShippingMethod,
        setSelectedService,
        setPurchaseTierFilter,
        setIsExpressPrescriptionReview,
        setSelectedApprovalService,
        isSelfPickupFlow,
        trackSelfPickupView,
      } = chooseTreatmentContext;

      setShippingMethod(constants.PICKUP);
      setSelectedService(
        isSelfPickupFlow
          ? constants.SERVICE_PRESCRIPTION_AND_MEDICATION
          : constants.SERVICE_PRESCRIPTION_ONLY
      );
      setPurchaseTierFilter(isSelfPickupFlow ? 'premium' : 'regular');
      setIsExpressPrescriptionReview(false);
      setSelectedApprovalService(constants.SERVICE_STANDARD_APPROVAL);
      trackSelfPickupView?.();

      goToStep(2, 500);
      return;
    }

    if (step < totalSteps) {
      goToStep(step + 1, 0);
      return;
    }

    // Last step finish
    document.body.style.overflow = '';
    onComplete();
  };

  const getStepContent = () => ({
    title: translate(language, 'choose_treatment', `walkthrough_step_${step}/title`),
    description: translate(language, 'choose_treatment', `walkthrough_step_${step}/description`),
    buttonText:
      step === totalSteps
        ? translate(language, 'choose_treatment', `walkthrough_step_${step}/finish_button`)
        : translate(language, 'choose_treatment', `walkthrough_step_${step}/continue_button`),
  });

  if (!isVisible) {
    document.body.style.overflow = '';
    hasTrackedShown.current = false;
    return null;
  }

  if (step === 1 && !hasTrackedShown.current) {
    hasTrackedShown.current = true;
    MainTracker.track('custom', 'walkthrough_shown', {
      user_id: customerState?.customerID || null,
      screen_name: 'Treatment Selection',
    });
  }

  const content = getStepContent();
  document.body.style.overflow = 'hidden';

  const padding = getPadding();
  const highlight1 = calculateHighlightWithPadding(highlightPosition, padding);
  const highlight2 = calculateHighlightWithPadding(highlightPosition2, padding);
  
  const highlightTop = highlight1.top;
  const highlightLeft = highlight1.left;
  const highlightWidth = highlight1.width;
  const highlightHeight = highlight1.height;
  
  const highlightTop2 = highlight2.top;
  const highlightLeft2 = highlight2.left;
  const highlightWidth2 = highlight2.width;
  const highlightHeight2 = highlight2.height;

  return createPortal(
    <div
      ref={overlayRef}
      className="walkthrough-overlay"
      onClick={(e) => e.target === overlayRef.current && undefined}
      onWheel={(e) => e.preventDefault()}
      onTouchMove={(e) => e.preventDefault()}
      onScroll={(e) => e.preventDefault()}
    >
      {/* Blur sections - cover entire screen except highlighted areas */}
      {(highlightPosition || highlightPosition2) ? (
        <>
          {/* Top blur - covers entire top area */}
          <div className="blur-section blur-top" style={{ 
            height: highlightPosition && highlightPosition2 
              ? `${Math.min(highlightTop, highlightTop2)}px` 
              : highlightPosition 
                ? `${highlightTop}px` 
                : highlightPosition2 
                  ? `${highlightTop2}px` 
                  : '0px'
          }} />
          
          {/* Bottom blur - covers entire bottom area */}
          <div
            className="blur-section blur-bottom"
            style={{
              top: highlightPosition && highlightPosition2
                ? `${Math.max(highlightTop + highlightHeight, highlightTop2 + highlightHeight2)}px`
                : highlightPosition
                  ? `${highlightTop + highlightHeight}px`
                  : highlightPosition2
                    ? `${highlightTop2 + highlightHeight2}px`
                    : '0px',
              height: highlightPosition && highlightPosition2
                ? `calc(100vh - ${Math.max(highlightTop + highlightHeight, highlightTop2 + highlightHeight2)}px)`
                : highlightPosition
                  ? `calc(100vh - ${highlightTop + highlightHeight}px)`
                  : highlightPosition2
                    ? `calc(100vh - ${highlightTop2 + highlightHeight2}px)`
                    : '100vh',
            }}
          />
          
          {/* Left blur sections - cover left areas between highlights */}
          {highlightPosition && (
            <div
              className="blur-section blur-left"
              style={{ 
                top: `${highlightTop}px`, 
                width: `${highlightLeft}px`, 
                height: `${highlightHeight}px` 
              }}
            />
          )}
          {highlightPosition2 && (
            <div
              className="blur-section blur-left"
              style={{ 
                top: `${highlightTop2}px`, 
                width: `${highlightLeft2}px`, 
                height: `${highlightHeight2}px` 
              }}
            />
          )}
          
          {/* Right blur sections - cover right areas between highlights */}
          {highlightPosition && (
            <div
              className="blur-section blur-right"
              style={{
                top: `${highlightTop}px`,
                left: `${highlightLeft + highlightWidth}px`,
                width: `calc(100vw - ${highlightLeft + highlightWidth}px)`,
                height: `${highlightHeight}px`,
              }}
            />
          )}
          {highlightPosition2 && (
            <div
              className="blur-section blur-right"
              style={{
                top: `${highlightTop2}px`,
                left: `${highlightLeft2 + highlightWidth2}px`,
                width: `calc(100vw - ${highlightLeft2 + highlightWidth2}px)`,
                height: `${highlightHeight2}px`,
              }}
            />
          )}
          
          {/* Middle blur sections - cover areas between the two highlights if they're on different rows */}
          {highlightPosition && highlightPosition2 && (
            <>
              {/* Blur between highlights horizontally if they overlap vertically */}
              {highlightTop2 > highlightTop + highlightHeight && (
                <div
                  className="blur-section"
                  style={{
                    top: `${highlightTop + highlightHeight}px`,
                    left: '0px',
                    width: '100vw',
                    height: `${highlightTop2 - (highlightTop + highlightHeight)}px`,
                  }}
                />
              )}
              {highlightTop > highlightTop2 + highlightHeight2 && (
                <div
                  className="blur-section"
                  style={{
                    top: `${highlightTop2 + highlightHeight2}px`,
                    left: '0px',
                    width: '100vw',
                    height: `${highlightTop - (highlightTop2 + highlightHeight2)}px`,
                  }}
                />
              )}
              {/* Blur between highlights vertically if they overlap horizontally */}
              {highlightLeft2 > highlightLeft + highlightWidth && highlightTop2 <= highlightTop + highlightHeight && highlightTop2 + highlightHeight2 >= highlightTop && (
                <div
                  className="blur-section"
                  style={{
                    top: `${Math.max(highlightTop, highlightTop2)}px`,
                    left: `${highlightLeft + highlightWidth}px`,
                    width: `${highlightLeft2 - (highlightLeft + highlightWidth)}px`,
                    height: `${Math.min(highlightTop + highlightHeight, highlightTop2 + highlightHeight2) - Math.max(highlightTop, highlightTop2)}px`,
                  }}
                />
              )}
              {highlightLeft > highlightLeft2 + highlightWidth2 && highlightTop2 <= highlightTop + highlightHeight && highlightTop2 + highlightHeight2 >= highlightTop && (
                <div
                  className="blur-section"
                  style={{
                    top: `${Math.max(highlightTop, highlightTop2)}px`,
                    left: `${highlightLeft2 + highlightWidth2}px`,
                    width: `${highlightLeft - (highlightLeft2 + highlightWidth2)}px`,
                    height: `${Math.min(highlightTop + highlightHeight, highlightTop2 + highlightHeight2) - Math.max(highlightTop, highlightTop2)}px`,
                  }}
                />
              )}
            </>
          )}
        </>
      ) : (
        /* Blur entire screen during transition */
        <div className="blur-section" style={{ top: 0, left: 0, width: '100vw', height: '100vh' }} />
      )}

      {/* Dark overlay with cutout */}
      <svg className="walkthrough-svg">
        <defs>
          <mask id="walkthrough-mask">
            <rect width="100%" height="100%" fill="white" />
            {highlightPosition && (
              <rect
                x={highlightLeft}
                y={highlightTop}
                width={highlightWidth}
                height={highlightHeight}
                fill="black"
                rx="20"
              />
            )}
            {highlightPosition2 && (
              <rect
                x={highlightLeft2}
                y={highlightTop2}
                width={highlightWidth2}
                height={highlightHeight2}
                fill="black"
                rx="20"
              />
            )}
          </mask>
        </defs>
        <rect width="100%" height="100%" className="walkthrough-mask-rect" mask="url(#walkthrough-mask)" />
      </svg>

      {/* Highlight borders */}
      {highlightPosition && (
        <div
          className="highlight-border"
          style={{
            top: highlightTop,
            left: highlightLeft,
            width: highlightWidth,
            height: highlightHeight,
          }}
        />
      )}
      {highlightPosition2 && (
        <div
          className="highlight-border"
          style={{
            top: highlightTop2,
            left: highlightLeft2,
            width: highlightWidth2,
            height: highlightHeight2,
          }}
        />
      )}

      {/* Confetti animation overlay */}
      {isConfettiStep && (
        <div className="confetti-overlay" ref={confettiRef} />
      )}

      {/* Modal */}
      {tooltipPosition && !isTransitioning && (
        <div
          ref={tooltipRef}
          className={`walkthrough-modal ${(step === 4 || step === 5) ? 'step-3' : ''}`}
          style={{
            top: tooltipPosition.top,
            ...(isMobile
              ? {
                  left: 0,
                  right: 0,
                  width: 'auto',
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  maxWidth: (step === 4 || step === 5)
                    ? 'min(320px, calc(100% - 50px))'
                    : 'calc(100% - 50px)',
                }
              : { left: tooltipPosition.left }),
            transform: (!isMobile && (step === 4 || step === 5)) ? 'translateX(-50%)' : 'none',
            ...(isConfettiStep ? { width: 269 } : {}),
          }}
        >
          {step === 1 && isConfettiStep ? (
            <>
              <h3 className="step-title" style={{
                fontWeight: 600,
                fontSize: '14px',
                color: '#363636',
                lineHeight: '21px',
                margin: 0
              }}>
                {translate(language, 'choose_treatment', 'walkthrough_confetti_step/title')}
              </h3>
              <p className="step-description" style={{
                color: '#98A0AF',
                fontSize: '11px',
                lineHeight: '15px',
                margin: '4px 0 0 0'
              }}>
                {translate(language, 'choose_treatment', 'walkthrough_confetti_step/description')
                  .replace('{zipcode}', chooseTreatmentContext?.customerZipcode || '')}
              </p>
              <div className="button-container" style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '10px',
                width: '100%',
                marginTop: '20px'
              }}>
                <button
                  id="walkthrough-confetti-try-button"
                  onClick={handleConfettiTry}
                  style={{
                    flex: 1,
                    height: '36px',
                    padding: '0 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: 'none',
                    color: '#FFFFFF',
                    fontSize: '12px',
                    fontWeight: 600,
                    letterSpacing: '0.15px',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    background: 'linear-gradient(90deg, var(--primary-400, #269C6E) 0%, var(--secondary-400, #7ABA47) 100%)',
                  }}
                >
                  {translate(language, 'choose_treatment', 'walkthrough_confetti_step/try_button')}
                </button>
                <button
                  id="walkthrough-confetti-close-button"
                  onClick={handleSkip}
                  style={{
                    flex: 1,
                    height: '36px',
                    padding: '0 16px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'transparent',
                    border: '0px',
                    color: '#00734A',
                    fontSize: '14x',
                    fontWeight: 600,
                    letterSpacing: '0.15px',
                    cursor: 'pointer',
                    borderRadius: '4px',
                  }}
                >
                  {translate(language, 'choose_treatment', 'walkthrough_confetti_step/close_button')}
                </button>
              </div>
            </>
          ) : step === 1 ? (
            <>
              <h3 className="step-title" style={{
                fontWeight: 600,
                fontSize: '14px',
                color: '#363636',
                lineHeight: '21px',
                margin: 0
              }}>
                {translate(language, 'choose_treatment', 'walkthrough_step_1/title_new')}
              </h3>
              <p className="step-description" style={{
                color: '#98A0AF',
                fontSize: '11px',
                lineHeight: '15px',
                margin: '8px 0 0 0'
              }}>
                {translate(language, 'choose_treatment', 'walkthrough_step_1/description_new')}
              </p>
              <div className="button-container" style={{
                display: 'flex',
                flexDirection: isMobile ? 'row-reverse' : 'column',
                gap: '10px',
                width: '100%',
                marginTop: '8px'
              }}>
                <button
                  id="walkthrough-skip-button"
                  onClick={handleSkip}
                  style={{
                    width: '100%',
                    height: '36px',
                    ...(isMobile ? {} : { padding: '12px 24px' }),
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#61A333',
                    textAlign: 'center',
                    fontFamily: 'Poppins',
                    fontSize: '12px',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    lineHeight: '100%',
                    letterSpacing: '0.15px',
                    textDecorationLine: 'underline',
                    textDecorationStyle: 'solid',
                    textDecorationSkipInk: 'none',
                    textDecorationThickness: 'auto',
                    textUnderlineOffset: 'auto',
                    cursor: 'pointer',
                    borderRadius: '4px'
                  }}
                >
                  {translate(language, 'choose_treatment', 'walkthrough_step_1/skip_button_new')}
                </button>
                <button
                  id="walkthrough-continue-button"
                  onClick={handleContinue}
                  style={{
                    width: '100%',
                    lineHeight: '100%',
                    padding: '12px 24px',
                    background:'linear-gradient(90deg, var(--primary-400, #269C6E) 0%, var(--secondary-400, #7ABA47) 100%)',
                    border: 'none',
                    color: '#FFF',
                    fontSize: '14px',
                    fontWeight: 600,
                    letterSpacing: '0.15px',
                    cursor: 'pointer',
                    borderRadius: '8px',
                  }}
                >
                  {translate(language, 'choose_treatment', 'walkthrough_step_1/continue_button_new')}
                </button>
              </div>
            </>
          ) : step === 2 ? (
            <>
              <h3 className="step-title-large">{content.title}</h3>
              <p className="step-description">{content.description}</p>
              <div className="button-container single">
                <button onClick={handleContinue} className="btn-primary half-width">
                  {content.buttonText}
                </button>
              </div>
            </>
          ) : step === 3 ? (
            <>
              <h3 className="step-title-large">{content.title}</h3>
              <p className="step-description step-2">
                <span>{translate(language, 'choose_treatment', 'walkthrough_step_3/description_part1')}</span>
                <span className="step-description-highlight">
                  {translate(language, 'choose_treatment', 'walkthrough_step_3/description_highlight')}
                </span>
                <span>{translate(language, 'choose_treatment', 'walkthrough_step_3/description_part2')}</span>
              </p>
              <div className="button-container" style={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: '12px',
                width: '100%'
              }}>
                <button 
                  onClick={handleSkip} 
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#61A333',
                    textAlign: 'center',
                    fontFamily: 'Poppins',
                    fontSize: '12px',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    lineHeight: '100%',
                    letterSpacing: '0.15px',
                    textDecorationLine: 'underline',
                    textDecorationStyle: 'solid',
                    textDecorationSkipInk: 'none',
                    textDecorationThickness: 'auto',
                    textUnderlineOffset: 'auto',
                    cursor: 'pointer',
                    borderRadius: '8px',
                  }}
                >
                  {translate(language, 'choose_treatment', 'walkthrough_step_1/skip_button_new')}
                </button>
                <button 
                  onClick={handleContinue} 
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'linear-gradient(90deg, var(--primary-400, #269C6E) 0%, var(--secondary-400, #7ABA47) 100%)',
                    border: 'none',
                    color: '#FFF',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    borderRadius: '8px',
                    lineHeight: '100%',
                  }}
                >
                  {content.buttonText}
                </button>
              </div>
            </>
          ) : step === 4 ? (
            <>
              <h3 className="step-title-large">{content.title}</h3>
              <p className="step-description">{content.description}</p>
              <div className="button-container" style={{ 
                display: 'flex', 
                flexDirection: 'column',
                gap: '12px',
                width: '100%'
              }}>
                <button 
                  onClick={handleSkip} 
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    backgroundColor: 'transparent',
                    border: 'none',
                    color: '#61A333',
                    textAlign: 'center',
                    fontFamily: 'Poppins',
                    fontSize: '12px',
                    fontStyle: 'normal',
                    fontWeight: 600,
                    lineHeight: '100%',
                    letterSpacing: '0.15px',
                    textDecorationLine: 'underline',
                    textDecorationStyle: 'solid',
                    textDecorationSkipInk: 'none',
                    textDecorationThickness: 'auto',
                    textUnderlineOffset: 'auto',
                    cursor: 'pointer',
                    borderRadius: '8px'
                  }}
                >
                  {translate(language, 'choose_treatment', 'walkthrough_step_1/skip_button_new')}
                </button>
                <button 
                  onClick={handleContinue} 
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    background: 'linear-gradient(90deg, var(--primary-400, #269C6E) 0%, var(--secondary-400, #7ABA47) 100%)',
                    border: 'none',
                    color: '#FFF',
                    fontSize: '14px',
                    fontWeight: 600,
                    cursor: 'pointer',
                    borderRadius: '8px',
                    lineHeight: '100%',
                  }}
                >
                  {content.buttonText}
                </button>
              </div>
            </>
          ) : (
            <>
              <h3 className="step-title-large">{content.title}</h3>
              <p className="step-description">{content.description}</p>
              <div className="button-container single">
                <button onClick={handleContinue} className="btn-primary half-width">
                  {content.buttonText}
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>,
    document.body
  );
};

export default SelfPickupWalkthrough;